<template>
  <Table
    :tableHeaders="tableHeadersComputed"
    :tableData="tableData"
    :initialValues="initialValues"
    :pageLength="pageLength"
    :isLoading="isLoading"
    @onPageClick="onPageClick"
    @selectedNumberOfRow="selectedNumberOfRow"
  >
    <template #tableRow="{ request }">
      <tr class="ltr:font-sans text-xs border-b border-brand-gray-300 w-full">
        <td class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300">
          <div class="flex items-center gap-4">
            <span class="text-primary cursor-pointer">
              <i
                :class="[request?.id === selectedDraft ? 'fa-minus-circle' : 'fa-plus-circle']"
                class="fa"
                @click.prevent="toggleIcons(request)"
              ></i>
            </span>
            <div class="flex items-center">
              <CheckBox
                :checked="selectedId.includes(request?.id)"
                :value="selectedId.includes(request?.id)"
                :id="request?.id"
                :selectedId="selectedId"
                @select="selectAllFields(request)"
              />
            </div>
          </div>
        </td>
        <slot name="tableRow" :request="request">
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.requestNumber }}
          </td>
          <td
            class="min-w-[180px] px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span v-html="checkOwnerName(request)"></span>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.lookupPersonWhitelistId }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.lookupLastActionId }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ formattedCreationDate(request?.creationDate) }}
          </td>
        </slot>

        <td class="px-6 py-2">
          <div class="flex items-center justify-center">
            <div class="flex items-center justify-center gap-2 size-10" v-if="isShowLockFeature">
              <button
                class="size-6 rounded-md text-white cursor-pointer"
                v-if="!request.checkedOutBy || (isEstablishmentAdmin && !request.isCheckedOutByMe)"
                @click.prevent="checkOut(request?.requestNumber)"
              >
                <i class="fa fa-unlock text-green-600"></i>
              </button>
              <button
                v-if="request.isCheckedOutByMe || isEstablishmentAdmin"
                class="size-6 rounded-md cursor-pointer"
                :class="[!request.isCheckedOutByMe ? 'text-black' : 'text-green-600']"
                @click.prevent="checkIn(request)"
              >
                <i class="fa fa-lock"></i>
              </button>
            </div>
            <div class="flex items-center justify-center gap-2 cursor-pointer w-20 h-10">
              <slot name="tableOperation" :request="request">
                <button
                  class="bg-primary size-6 rounded-md text-white"
                  @click.prevent="complete(request)"
                  v-tippy="{ content: $t('Complete'), theme: 'dark-border' }"
                >
                  <i class="fa fa-edit"></i>
                </button>
                <button
                  class="text-primary border border-primary size-6 rounded-md cursor-pointer"
                  @click.prevent="deleteSelected(request)"
                  v-tippy="{ content: $t('Delete'), theme: 'dark-border' }"
                >
                  <i class="fa fa-trash"></i>
                </button>
                <button
                  class="bg-primary size-6 rounded-md text-white cursor-pointer"
                  @click.prevent="viewTicket(request)"
                  v-tippy="{ content: $t('Report an issue'), theme: 'dark-border' }"
                >
                  <i class="fa fa-exclamation-triangle"></i>
                </button>
              </slot>
            </div>
          </div>
        </td>
      </tr>
      <slot name="extendRow" :request="request" v-if="request?.id === selectedDraft">
        <ValidUsers
          :requestDraft="request"
          :genderText="getGenderText(request.request?.genderId)"
        />
      </slot>
    </template>
    <template #moreTableRow="{ request }">
      <slot name="moreTableRow" :request="request"></slot>
    </template>
  </Table>
</template>

<script setup>
import Table from '@/components/Table/index.vue'
import CheckBox from '@/components/Checkbox/index.vue'
import ValidUsers from '@/views/individual/draft/ValidUser.vue'
import { onMounted, ref, computed } from 'vue'
import lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import apiHelperService from '@/services'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'

const i18n = useI18n()
const router = useRouter()

const emits = defineEmits(['onPageClick', 'seleted', 'checked', 'checkOut', 'checkIn', 'onChange'])

const props = defineProps({
  tableData: {
    type: Array,
    default: () => []
  },
  initialValues: {
    type: Object,
    default: () => {}
  },
  pageLength: {
    type: Number,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  showModal: {
    type: Boolean,
    default: false
  },
  tableHeaders: {
    type: Array,
    default: () => []
  },
  isShowLockFeature: {
    type: Boolean,
    default: true
  }
})

const isServiceCenters = ref(false)
const isEstablishmentAdmin = ref(false)
const isSkipPayment = ref(false)
const selectedDraft = ref({})
const selectedId = ref([])
const selectedCheckboxes = ref(new Set())
const isRelatedRequestServiceCalled = ref(false)
const genders = ref([])
const serviceConfig = ref()

const tableHeaders = ref([i18n.t('Type'), i18n.t('availableAmount'), i18n.t('refundInformation')])

const tableHeadersComputed = computed(() => {
  return props.tableHeaders || tableHeaders.value
})

const {
  issuePassport,
  replacePassport,
  naturalizationRequest,
  updateMobileNumber,
  femaleChildrenNaturalizationRequest,
  citizenWifeNaturalizationRequest,
  familyBookAddChild,
  familyBookAddWifeOrHusband,
  newBornPackage,
  nonCitizenWifeGuardianship,
  issueLeavePermitForExemption,
  cancelLeavePermit,
  passportLosing,
  LocalOrGccCitizenSponsorShipReportEscape,
  LocalOrGccCitizenSponsorShipRemoveReportEscape,
  ResidenceRenewalForFamilyMembersDiplomaticSponsor,
  ResidenceIssuanceForFamilyMembersDiplomaticSponsor,
  entryExit,
  reportsResidenceDetails,
  companyVisitTransferToCourtRequest,
  companyTransferToCourtRequest,
  NewBornVisaHolders,
  companyTransferTtoCourtRequest,
  refundFees,
  icaRefund,
  guardianship,
  issueNewFamilyBookDueDivorcing,
  dubaiResidentsNewService,
  dubaiResidentsRenewService,
  dubaiResidentsReplaceId,
  dubaiResidentsNewBorn,
  issueNewEmiratesIDForDiplomatic,
  renewEmiratesIDForDiplomatic,
  replaceEmiratesIDForDiplomatic,
  citizenMothersNameModification,
  citizenBirthdateModification,
  citizenAgeEstimation,
  citizenFirstNameModification,
  citizenAddingAnExistingNickname,
  exceptionalRefund,
  guardianshipFileCancellation
} = lookups.ServiceTransactions

const {
  issueNewIDForGccCitizen,
  renewIDForGccCitizen,
  replaceIDCardForGccCitizen,
  renewIDForCitizen,
  replaceIDCardForCitizen,
  citizensFirstTimeIssue,
  citizensNewbornRegistertion
} = lookups.eidaServices

const { eidaServiceTransactions, esbRequiredServiceTransactions } = client_config

const firstOwnerValidityCase = [
  issuePassport,
  replacePassport,
  naturalizationRequest,
  updateMobileNumber,
  femaleChildrenNaturalizationRequest,
  citizenWifeNaturalizationRequest,
  familyBookAddChild,
  familyBookAddWifeOrHusband,
  newBornPackage,
  nonCitizenWifeGuardianship,
  passportLosing,
  issueLeavePermitForExemption,
  cancelLeavePermit,
  LocalOrGccCitizenSponsorShipReportEscape,
  LocalOrGccCitizenSponsorShipRemoveReportEscape,
  ResidenceRenewalForFamilyMembersDiplomaticSponsor,
  ResidenceIssuanceForFamilyMembersDiplomaticSponsor
]

const secondOwnerValidityCase = [
  entryExit,
  reportsResidenceDetails,
  companyVisitTransferToCourtRequest,
  companyTransferToCourtRequest,
  NewBornVisaHolders,
  companyTransferTtoCourtRequest,
  refundFees,
  icaRefund,
  issueNewEmiratesIDForDiplomatic,
  guardianship,
  issueNewFamilyBookDueDivorcing,
  dubaiResidentsNewService,
  dubaiResidentsRenewService,
  dubaiResidentsReplaceId,
  dubaiResidentsNewBorn,
  renewEmiratesIDForDiplomatic,
  replaceEmiratesIDForDiplomatic,
  issueNewIDForGccCitizen,
  renewIDForGccCitizen,
  replaceIDCardForGccCitizen,
  renewIDForCitizen,
  replaceIDCardForCitizen,
  citizensFirstTimeIssue,
  citizensNewbornRegistertion
]

const thirdOwnerValidityCase = [exceptionalRefund]

const fourthOwnerValidityCase = [
  citizenMothersNameModification,
  citizenBirthdateModification,
  citizenAgeEstimation,
  citizenFirstNameModification,
  citizenAddingAnExistingNickname
]

const getGenderText = (genderId) => {
  if (!genderId) return
  if (genders.value) {
    const text = genders.value.find((gender) => gender.id == genderId)
    if (text) return text.text
  }
}

const onPageClick = (pageNum) => {
  emits('onPageClick', pageNum)
}

const selectedNumberOfRow = (num) => {
  emits('selected', num)
}

const checkOwnerName = (draft) => {
  if (!draft) return
  const { arabicName, englishName, arabicApplicantName, englishApplicantName } = draft.request
  const { serviceTransactionId } = draft
  let ownerName = ''
  if (firstOwnerValidityCase.includes(serviceTransactionId)) {
    if (arabicName || englishName) {
      ownerName = arabicName + '<br />' + englishName
    }
  }

  if (secondOwnerValidityCase.includes(serviceTransactionId)) {
    if (arabicName || englishName) {
      ownerName =
        arabicApplicantName + '<br />' + englishApplicantName ??
        arabicApplicantName + '<br>' + arabicApplicantName
    }
  }

  if (thirdOwnerValidityCase.includes(serviceTransactionId)) {
    ownerName =
      arabicApplicantName + '<br />' + englishApplicantName ??
      arabicApplicantName + '<br>' + arabicApplicantName
  }

  if (ownerName === 'NA' || ownerName === 'غير موجود') {
    if (fourthOwnerValidityCase.includes(serviceTransactionId)) {
      if (arabicName || englishName) {
        ownerName =
          arabicName + '<br>' + englishName ?? arabicApplicantName + '<br>' + arabicApplicantName
      } else {
        ownerName =
          arabicApplicantName + '<br>' + englishApplicantName ??
          arabicApplicantName + '<br>' + arabicApplicantName
      }
    }
  }
  ownerName = arabicName + '<br>' + englishName
  return ownerName
}

const toggleIcons = (draft) => {
  if (selectedDraft.value === draft.id) {
    selectedDraft.value = null
  } else {
    selectedDraft.value = draft.id
  }
}

const selectAllFields = (requestDraft) => {
  if (!requestDraft) return
  if (selectedCheckboxes.value.has(requestDraft.id)) {
    selectedCheckboxes.value.delete(requestDraft.id)
  } else {
    selectedCheckboxes.value.add(requestDraft.id)
  }
  selectedId.value = Array.from(selectedCheckboxes.value)

  emits('checked', selectedId.value)
}

const formattedCreationDate = (date) => {
  if (!date) return
  return DateTime.fromISO(date).toFormat('dd/MM/yyyy hh:mm a')
}

const checkOut = async (draftNumber) => {
  if (!draftNumber) return
  try {
    const result = await apiHelperService.get(`/api/draft/${draftNumber}/checkOut`)
    emits('checkOut')
  } catch (error) {
    console.error('checkOut', error)
  }
}

const checkIn = async (requestDraft) => {
  const { isCheckedOutByMe, requestNumber } = requestDraft
  if (!isCheckedOutByMe) return
  try {
    const result = await apiHelperService.get(`/api/draft/${requestNumber}/checkIn`)
    emits('checkIn')
  } catch (error) {
    console.error('checkOut', error)
  }
}

const complete = async (draft) => {
  if (!draft) return
  const response = await checkRequestIfHasRelatedRequest(draft)
  if (response && response?.relatedPaymentProcessRequest) {
    const paymentResults = response?.relatedPaymentProcessRequest.map((val) => {
      return {
        requestNumber: val.transactionRef,
        receiptNumber: val.receiptNumber,
        serviceName: val.serviceName,
        isSkipPayment: isSkipPayment.value
      }
    })
    // Todo: paymentResults later no data available
  } else {
    openDraft(draft)
  }
}

const openDraft = (draft) => {
  const { serviceTransactionId, requestNumber, isGuardian, request } = draft
  const { secondaryRequestUrl } = serviceConfig.value || {}

  const baseRoute = {
    name: 'ServiceForm',
    params: { serviceId: serviceTransactionId },
    query: { requestNumber }
  }
  if (serviceConfig.value && secondaryRequestUrl && request?.createdBySecondaryForm) {
    router.push({
      ...baseRoute,
      params: { ...baseRoute.params, serviceId: serviceTransactionId }
    })
  } else if (serviceTransactionId === guardianshipFileCancellation) {
    router.push({
      ...baseRoute,
      query: { ...baseRoute.query, isGuardian }
    })
  } else {
    router.push(baseRoute)
  }
}

const completeProcess = async (draft) => {
  const { requestNumber } = draft
  if (!requestNumber) return
  try {
    const payload = {
      draftNumber: requestNumber
    }
    const result = await apiHelperService.post('/api/CompleteProcess', payload)
    if (result) {
      return result
    }
  } catch (error) {
    console.error('completeProcess', error)
  }
}

const paymentProcess = async (draft) => {
  const { requestNumber } = draft
  if (!requestNumber) return
  try {
    const payload = {
      draftNumber: requestNumber
    }
    const result = await apiHelperService.post('/api/payment/complete', payload)
    if (result) {
      return result
    }
  } catch (error) {
    console.error('completeProcess', error)
  }
}

const checkRequestIfHasRelatedRequest = async (request) => {
  if (!request) return
  let tempServiceTransactionId
  const { serviceTransactionId, administrativeRegionId, mainServiceTransactionId, requestNumber } =
    request
  try {
    const response = await apiHelperService.get(
      'api/requestmaster/' + requestNumber + '/relatedRequests'
    )
    if (response) {
      response.map(async (request, i) => {
        tempServiceTransactionId = serviceTransactionId
        if (eidaServiceTransactions.includes(serviceTransactionId)) {
          tempServiceTransactionId = request[i].serviceTransactionId

          serviceConfig.value = await loadConfig(serviceTransactionId.value, administrativeRegionId)
          tempServiceTransactionId = mainServiceTransactionId
          if (esbRequiredServiceTransactions.indexOf(+serviceTransactionId) >= 0) {
            completeProcess(request)
          } else {
            paymentProcess(request)
          }
          isRelatedRequestServiceCalled.value = true
        }
      })
    }

    serviceConfig.value = await loadConfig(tempServiceTransactionId, administrativeRegionId)
    return response
  } catch (error) {
    console.error('checkOut', error)
  }
}

const deleteSelected = async (requestDraft) => {
  selectedCheckboxes.value.add(requestDraft.id)
  selectedId.value = Array.from(selectedCheckboxes.value)
  emits('delete', selectedId.value)
}

const viewTicket = (requestDraft) => {
  // TODO: report an issue route not completed pending to do.
  router.push({
    path: `/ticket`,
    query: {
      requestNumber: requestDraft.requestNumber,
      serviceTransactionId: requestDraft.serviceTransactionId
    }
  })
}

const loadConfig = async (serviceTransactionId, administrativeRegionId) => {
  const params = {
    serviceTransactionId,
    administrativeRegionId
  }
  const result = await apiHelperService.get('/api/service/transactions/config', { params })
  if (result) {
    return result
  }
}

const loadGenders = async () => {
  try {
    const response = await apiHelperService.get('/api/lookup/genders')
    genders.value = response
  } catch (error) {
    console.error('loadGenders', error)
  }
}

defineExpose({
  checkOwnerName,
  formattedCreationDate,
  getGenderText
})

onMounted(async () => {
  await loadGenders()
})
</script>

<style></style>
